import EmptyList from '@components/EmptyList'
import { SidebarMobileToggle } from '@components/Sidebar/MobileToggle'
import { useInfiniteScroll } from '@hooks/useInfiniteScroll'
import {
  ActivityLabel,
  UserActivityListItem,
} from '@modules/UserActivity/Components/ActivityListItem'
import { UserActivityFilter } from '@plug-core/clients/LedgerScannerClient'
import { useUserActivities } from '@plug-core/hooks/useUserActivities'
import { PlugUserActivity } from '@plug-core/interfaces/transactionActivity'
import { Heading, ListGroup, Sheet, cn } from '@ui'
import { getWindowDimensions } from '@utils/windows'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserActivityDetails } from './Components/ActivityDetails'

export const UserActivity = ({
  filter,
  nested,
  hideEmpty,
}: {
  filter?: UserActivityFilter
  nested?: boolean
  hideEmpty?: boolean
}) => {
  const [displayedActivity, setDisplayedActivity] = useState<PlugUserActivity>()

  // Keeps the sheet content in place while it animates out.
  const lastDisplayedActivity = useRef<PlugUserActivity>()
  if (displayedActivity) lastDisplayedActivity.current = displayedActivity
  const sheetActivity = displayedActivity ?? lastDisplayedActivity.current

  const containerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { userActivity, groupUserActivityByDate, fetchNextPage } = useUserActivities(
    filter,
    nested ? 5 : 100
  )

  useInfiniteScroll({
    containerRef,
    onNearBottom: fetchNextPage,
    enabled: !nested,
  })

  if (!userActivity) return

  const renderGroupedActivities = () => {
    return groupUserActivityByDate().map(([date, activities]) => (
      <section key={date} className="mb-5 flex shrink-0 flex-col gap-2">
        <Heading level={5} className="px-1">
          {date}
        </Heading>

        <ListGroup>
          {activities.map((activity) => (
            <UserActivityListItem
              key={activity.id}
              activity={activity}
              onClick={(activity) => setDisplayedActivity(activity)}
            />
          ))}
        </ListGroup>
      </section>
    ))
  }

  if (hideEmpty && !userActivity.length) return

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex flex-col no-scrollbar',
        displayedActivity ? 'overflow-hidden' : 'overflow-scroll'
      )}
      style={{ height: nested ? undefined : getWindowDimensions().height - 48 }}
    >
      <SidebarMobileToggle title="Activity" />

      <Sheet
        open={!!displayedActivity}
        onClose={() => setDisplayedActivity(undefined)}
        title={sheetActivity && <ActivityLabel activity={sheetActivity} />}
        hideTitle
      >
        {sheetActivity && (
          <UserActivityDetails
            activity={sheetActivity}
            onClose={() => setDisplayedActivity(undefined)}
          />
        )}
      </Sheet>

      {nested && (
        <Heading level={5} className="mb-2 px-1">
          {t('tokenDetails.lastTransactions')}
        </Heading>
      )}

      {!userActivity.length && (
        <div className="mt-8">
          <EmptyList
            emoji="🥹"
            title={t('transactionHistory.empty')}
            description={t('transactionHistory.emptyMessage')}
          />
        </div>
      )}

      {!nested ? (
        renderGroupedActivities()
      ) : userActivity.length ? (
        <ListGroup className="shrink-0">
          {userActivity.map((activity) => (
            <UserActivityListItem
              key={activity.id}
              activity={activity}
              onClick={(activity) => setDisplayedActivity(activity)}
            />
          ))}
        </ListGroup>
      ) : null}
    </div>
  )
}
